/*
<summary>
   This functional component "FormikDateFieldMUI" is a Formik-integrated MUI date field that
   lets the user type a date directly with the keyboard, in addition to picking one from the
   calendar.
   Developer: Aashish Singh, Created Date: 23-Oct-2026
</summary>
<param>Formik field name plus optional label/disabled/min/max</param>
<returns>Returns JSX</returns>
*/
 
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useField } from 'formik';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import { DateFormatNew } from '../../constants/date-picker-constant';
import preferencesStore from '../../core/stores/preferences-store';
import { formatMessage } from '../../translations/format-message';
 
const dayjsInputFormat = DateFormatNew.toUpperCase();
 
interface IProps {
  name: string;
  label?: string;
  disabled?: boolean;
  min?: string | Date;
  max?: string | Date;
  disableFuture?: boolean;
  disablePastDate?: boolean;
}
 
const FormikDateFieldMUI = (props: IProps) => {
  const { name, label, disabled, min, max, disableFuture, disablePastDate } = props;
  const [field, meta, helpers] = useField(name);
  const { touched, error } = meta;
  // Laptop and below: compact calendar (see .tool-date-field-mui-popper-compact in style.css)
  const isCompact = useMediaQuery('(max-width: 1400px)');
  const popperRef = useRef<any>(null);

  // Popup size changes when the breakpoint flips, so re-position the open popper
  useEffect(() => {
    popperRef.current?.update();
  }, [isCompact]);
 
  return (
    <>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={preferencesStore.getLanguage === 'en' ? 'en' : 'ja'}
      >
        <Stack spacing={3} className="tool-date-field-mui">
          <DesktopDatePicker
            label={label}
            views={['year', 'month', 'day']}
            inputFormat={dayjsInputFormat}
            value={field.value ? dayjs(field.value) : null}
            onChange={(newValue: Dayjs | null) => {
              helpers.setValue(newValue ? newValue.toDate() : null);
              helpers.setTouched(true);
            }}
            disableFuture={disableFuture}
            disablePast={disablePastDate}
            disabled={disabled}
            minDate={min ? dayjs(min) : undefined}
            maxDate={max ? dayjs(max) : undefined}
            renderInput={(params: any) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, placeholder: DateFormatNew }}
              />
            )}
            PopperProps={{
              className: `tool-date-field-mui-popper${isCompact ? ' tool-date-field-mui-popper-compact' : ''}`,
              popperRef,
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'flip',
                  enabled: true,
                  options: {
                    fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
                    padding: 8,
                  },
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: { altAxis: true, tether: false, padding: 8 },
                },
              ],
            }}
          />
        </Stack>
      </LocalizationProvider>
      {touched && error && (
        <span data-testid="testError" className="text-danger errorMessage">
          {formatMessage(error)}
        </span>
      )}
    </>
  );
};
 
export default observer(FormikDateFieldMUI);